import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import {
  getBillingUsersRequest,
  resolveBillingUsersLinks,
  getBillingUserRequest,
  addBillingUserRequest,
  updateBillingUserRequest
} from "Api/billingSystem";
import type {
  AddBillingUserPayload,
  UpdateBillingUserPayload
} from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type {
  BillingUsersResponse,
  BillingUser,
  BillingUserData
} from "Api/billingSystem/types";
import type { RefUser } from "Api/ref/User.types";

// Thunks
export const getBillingUsers = createAppAsyncThunk(
  "billingSystem/users/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      const billingUsers = await getBillingUsersRequest(billingProfileId);
      const api = resolveBillingUsersLinks(billingUsers);
      // Best-effort: a failing ref endpoint shouldn't hide the whole user
      // list — the table renders user_id fallbacks when ref data is missing.
      let users: { [userId: string]: RefUser } | undefined;
      try {
        users = await api.users?.();
      } catch (err) {
        logger(err);
      }
      return { billingUsers, users };
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getBillingUser = createAppAsyncThunk(
  "billingSystem/users/getOne",
  async (
    {
      billingProfileId,
      billingUserId
    }: {
      billingProfileId: string;
      billingUserId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await getBillingUserRequest(billingProfileId, billingUserId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const addBillingUser = createAppAsyncThunk(
  "billingSystem/users/add",
  async (
    {
      billingProfileId,
      payload
    }: {
      billingProfileId: string;
      payload: AddBillingUserPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      return await addBillingUserRequest(billingProfileId, payload);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const updateBillingUser = createAppAsyncThunk(
  "billingSystem/users/update",
  async (
    {
      billingProfileId,
      billingUserId,
      payload
    }: {
      billingProfileId: string;
      billingUserId: string;
      payload: UpdateBillingUserPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      return await updateBillingUserRequest(
        billingProfileId,
        billingUserId,
        payload
      );
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type BillingUserModalMode = "add" | "edit";

type BillingUsersState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: BillingUserData[];
      error?: string;
    }
  >;
  byId: Record<
    string,
    {
      loading: boolean;
      data?: BillingUser;
      error?: string;
    }
  >;
  addUserState: {
    loading: boolean;
    error?: string;
    success: boolean;
  };
  updateUserState: {
    loading: boolean;
    error?: string;
    success: boolean;
  };
  billingUsersModal: {
    isOpen: boolean;
    mode: BillingUserModalMode;
    billingProfileId?: string;
    editingUser?: BillingUserData;
  };
};

const initialState: BillingUsersState = {
  byProfileId: {},
  byId: {},
  addUserState: {
    loading: false,
    error: undefined,
    success: false
  },
  updateUserState: {
    loading: false,
    error: undefined,
    success: false
  },
  billingUsersModal: {
    isOpen: false,
    mode: "add",
    billingProfileId: undefined,
    editingUser: undefined
  }
};

/** Joins both billing user and ref data into a single array of elements. */
const billingUserDataToArray = ({
  billingUsers,
  users
}: {
  billingUsers: BillingUsersResponse;
  users?: { [userId: string]: RefUser };
}): Array<BillingUserData> =>
  billingUsers.items.map(billingUser => ({
    billingUser,
    user: users?.[billingUser.user_id]
  }));

// Slice
const billingUsersSlice = createSlice({
  name: "billingUsers",
  initialState,
  reducers: {
    openAddUsersModal: (state, action: { payload: string }) => {
      state.billingUsersModal.isOpen = true;
      state.billingUsersModal.mode = "add";
      state.billingUsersModal.billingProfileId = action.payload;
      state.billingUsersModal.editingUser = undefined;
    },
    openEditUserModal: (
      state,
      action: { payload: { billingProfileId: string; user: BillingUserData } }
    ) => {
      state.billingUsersModal.isOpen = true;
      state.billingUsersModal.mode = "edit";
      state.billingUsersModal.billingProfileId =
        action.payload.billingProfileId;
      state.billingUsersModal.editingUser = action.payload.user;
    },
    closeBillingUsersModal: state => {
      state.billingUsersModal.isOpen = false;
      state.billingUsersModal.mode = "add";
      state.billingUsersModal.billingProfileId = undefined;
      state.billingUsersModal.editingUser = undefined;
      // Also reset user states when closing
      state.addUserState = {
        loading: false,
        error: undefined,
        success: false
      };
      state.updateUserState = {
        loading: false,
        error: undefined,
        success: false
      };
    }
  },
  extraReducers: builder => {
    // Get billing users by profile
    builder
      .addCase(getBillingUsers.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getBillingUsers.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);

        const fetched = billingUserDataToArray(action.payload);
        const fetchedIds = new Set(
          fetched.map(({ billingUser }) => billingUser.user_id)
        );
        const previous = state.byProfileId[billingProfileId]?.data ?? [];
        const previousById = new Map(
          previous.map(item => [item.billingUser.user_id, item])
        );

        // Carry over ref user data already resolved for users the refetch
        // returned without it, so names/emails don't flicker to the user_id
        // fallback on every reload.
        const merged = fetched.map(item => {
          if (item.user) return item;
          const prev = previousById.get(item.billingUser.user_id);
          return prev?.user ? { ...item, user: prev.user } : item;
        });
        // Keep users we already know about that this refetch didn't return.
        // The list endpoint is eventually consistent, so a refetch fired right
        // after adding a user often resolves before that user is queryable —
        // without this, the optimistically-added user would disappear until a
        // manual page refresh (CON-1310).
        const pending = previous.filter(
          ({ billingUser }) => !fetchedIds.has(billingUser.user_id)
        );

        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          [...merged, ...pending]
        );
      })
      .addCase(getBillingUsers.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });

    // Get single billing user
    builder
      .addCase(getBillingUser.pending, (state, { meta }) => {
        setDeep(state, ["byId", meta.arg.billingUserId, "loading"], true);
        setDeep(state, ["byId", meta.arg.billingUserId, "error"], undefined);
      })
      .addCase(getBillingUser.fulfilled, (state, action) => {
        const { billingUserId } = action.meta.arg;
        setDeep(state, ["byId", billingUserId, "loading"], false);
        setDeep(state, ["byId", billingUserId, "data"], action.payload);
      })
      .addCase(getBillingUser.rejected, (state, action) => {
        const { billingUserId } = action.meta.arg;
        setDeep(state, ["byId", billingUserId, "loading"], false);
        setDeep(state, ["byId", billingUserId, "error"], action.payload?.error);
      });

    // Add billing user
    builder
      .addCase(addBillingUser.pending, state => {
        state.addUserState.loading = true;
        state.addUserState.error = undefined;
        state.addUserState.success = false;
      })
      .addCase(addBillingUser.fulfilled, (state, action) => {
        state.addUserState.loading = false;
        state.addUserState.success = true;
        // Add the new user to the profile's user list if it exists.
        // Ref user data isn't returned by add, so it stays undefined until
        // the next list refetch resolves the ref:users:0 link.
        const { billingProfileId } = action.meta.arg;
        if (state.byProfileId[billingProfileId]?.data) {
          state.byProfileId[billingProfileId].data!.push({
            billingUser: action.payload,
            user: undefined
          });
        }
        // Also add to byId
        setDeep(
          state,
          ["byId", action.payload.user_id, "data"],
          action.payload
        );
      })
      .addCase(addBillingUser.rejected, (state, action) => {
        state.addUserState.loading = false;
        state.addUserState.error = action.payload?.error;
        state.addUserState.success = false;
      });

    // Update billing user
    builder
      .addCase(updateBillingUser.pending, state => {
        state.updateUserState.loading = true;
        state.updateUserState.error = undefined;
        state.updateUserState.success = false;
      })
      .addCase(updateBillingUser.fulfilled, (state, action) => {
        state.updateUserState.loading = false;
        state.updateUserState.success = true;
        // Update the user in the profile's user list if it exists, preserving
        // the previously resolved ref user data.
        const { billingProfileId, billingUserId } = action.meta.arg;
        const profileData = state.byProfileId[billingProfileId]?.data;
        if (profileData) {
          const userIndex = profileData.findIndex(
            item => item.billingUser.user_id === billingUserId
          );
          if (userIndex !== -1) {
            profileData[userIndex] = {
              ...profileData[userIndex],
              billingUser: action.payload
            };
          }
        }
        // Also update in byId
        setDeep(
          state,
          ["byId", action.payload.user_id, "data"],
          action.payload
        );
      })
      .addCase(updateBillingUser.rejected, (state, action) => {
        state.updateUserState.loading = false;
        state.updateUserState.error = action.payload?.error;
        state.updateUserState.success = false;
      });
  }
});

export const { openAddUsersModal, openEditUserModal, closeBillingUsersModal } =
  billingUsersSlice.actions;

export default billingUsersSlice.reducer;

// Selectors
const billingUsersStateSelector = (state: RootState) => state.billingUsers;

// Billing users by profile ID
export const selectProfileBillingUsers = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.data
);

export const selectProfileBillingUsersLoading = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileBillingUsersError = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

// Single billing user by ID
export const selectBillingUserById = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingUserId: string) => billingUserId,
  (state, billingUserId) => state.byId[billingUserId]?.data
);

export const selectBillingUserLoadingById = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingUserId: string) => billingUserId,
  (state, billingUserId) => state.byId[billingUserId]?.loading ?? false
);

export const selectBillingUserErrorById = createSelector(
  billingUsersStateSelector,
  (_: RootState, billingUserId: string) => billingUserId,
  (state, billingUserId) => state.byId[billingUserId]?.error
);

// Permission-based selectors
export const selectProfileBillingAdmins = createSelector(
  selectProfileBillingUsers,
  users => users?.filter(item => item.billingUser.level === "admin")
);

export const selectProfileBillingViewers = createSelector(
  selectProfileBillingUsers,
  users => users?.filter(item => item.billingUser.level === "view")
);

// Add user state selectors
export const selectAddUserLoading = createSelector(
  billingUsersStateSelector,
  state => state.addUserState.loading
);

export const selectAddUserError = createSelector(
  billingUsersStateSelector,
  state => state.addUserState.error
);

export const selectAddUserSuccess = createSelector(
  billingUsersStateSelector,
  state => state.addUserState.success
);

// Update user state selectors
export const selectUpdateUserLoading = createSelector(
  billingUsersStateSelector,
  state => state.updateUserState.loading
);

export const selectUpdateUserError = createSelector(
  billingUsersStateSelector,
  state => state.updateUserState.error
);

export const selectUpdateUserSuccess = createSelector(
  billingUsersStateSelector,
  state => state.updateUserState.success
);

// Billing users modal selectors
export const selectBillingUsersModalIsOpen = createSelector(
  billingUsersStateSelector,
  state => state.billingUsersModal.isOpen
);

export const selectBillingUsersModalMode = createSelector(
  billingUsersStateSelector,
  state => state.billingUsersModal.mode
);

export const selectBillingUsersModalBillingProfileId = createSelector(
  billingUsersStateSelector,
  state => state.billingUsersModal.billingProfileId
);

export const selectBillingUsersModalEditingUser = createSelector(
  billingUsersStateSelector,
  state => state.billingUsersModal.editingUser
);
